import { computed, type ComputedRef } from 'vue'
import {
  ACCOUNT_STATE,
  PAUSED_REASON,
  useAccountStatusQuery,
  type AccountStatus,
} from '@modules/account'
import { usePortfolioOverviewQuery } from '@modules/portfolio'

// Sub-variants for the "paused" state, mapped 1:1 from the backend's
// `pausedReason`:
//   - onboarding-rejected: rejected during initial review, never became active.
//   - active-suspended | active-deactivated: was active, later restricted.
//   - access-restricted: blocked-but-verified — assets intact, money actions
//     locked (the backend also sets accessRestricted=true for the Wallet / Bank
//     / Invest surfaces reachable via nav).
//   - kyc-rejected: KYC permanently rejected (admin reject, or any vendor
//     rejection/reverify). No self-service re-KYC — contact-us only.
export type PausedVariant =
  | 'onboarding-rejected'
  | 'active-suspended'
  | 'active-deactivated'
  | 'access-restricted'
  | 'kyc-rejected'

// Why re-KYC is needed — same screen shape, different copy:
//   expired  → "annual renewal" / renew flow
//   rejected → admin re-initiate / active re-verify — "resubmit" flow. (A genuine
//              rejection is terminal; it maps to the 'kyc-rejected' paused variant,
//              never here.)
export type RekycReason = 'expired' | 'rejected'

// Discriminated union the container switches on. Each `kind` maps 1-to-1 to a
// state component under `../components/states/`.
export type DashboardState =
  | { kind: 'onboarding-pending' }
  | { kind: 'in-review' }
  | { kind: 'approved-ready' }
  | { kind: 'invested' }
  | { kind: 'rekyc-required'; reason: RekycReason }
  | { kind: 'paused'; variant: PausedVariant }
  | { kind: 'regional-explore' }
  | { kind: 'guest' }
  | { kind: 'loading' }

const PAUSED_VARIANT: Record<string, PausedVariant> = {
  [PAUSED_REASON.ONBOARDING_REJECTED]: 'onboarding-rejected',
  [PAUSED_REASON.SUSPENDED]: 'active-suspended',
  [PAUSED_REASON.DEACTIVATED]: 'active-deactivated',
  [PAUSED_REASON.ACCESS_RESTRICTED]: 'access-restricted',
  [PAUSED_REASON.KYC_REJECTED]: 'kyc-rejected',
}

// The screen decision is owned by the backend (`accountState`); this maps that
// discriminator to the render `kind`. The ONLY derivation left on the client is
// the `active → invested | approved-ready` split, which depends on portfolio
// data the backend deliberately doesn't fetch.
export function resolveDashboardState(
  account: AccountStatus | undefined,
  totalInvested: number,
): DashboardState {
  if (!account) return { kind: 'loading' }

  // FE-only dormant path: non-Emirates-ID "regional explore" has no backend
  // backing yet, so it can't come through `accountState`. Kept as a short-circuit
  // until the backend ships the field (see profileTypes.isNonEmiratesIdHolder).
  if (account.isNonEmiratesIdHolder) return { kind: 'regional-explore' }

  switch (account.accountState) {
    case ACCOUNT_STATE.ONBOARDING_PENDING:
      return { kind: 'onboarding-pending' }
    case ACCOUNT_STATE.IN_REVIEW:
      return { kind: 'in-review' }
    case ACCOUNT_STATE.REKYC_EXPIRED:
      return { kind: 'rekyc-required', reason: 'expired' }
    case ACCOUNT_STATE.REKYC_REJECTED:
      return { kind: 'rekyc-required', reason: 'rejected' }
    case ACCOUNT_STATE.GUEST:
      return { kind: 'guest' }
    case ACCOUNT_STATE.PAUSED:
      return {
        kind: 'paused',
        variant: PAUSED_VARIANT[account.pausedReason ?? ''] ?? 'onboarding-rejected',
      }
    case ACCOUNT_STATE.ACTIVE:
      return totalInvested > 0 ? { kind: 'invested' } : { kind: 'approved-ready' }
    default:
      // Unexpected value — conservative: keep the account in review.
      return { kind: 'in-review' }
  }
}

export function useDashboardState(): ComputedRef<DashboardState> {
  const accountQuery = useAccountStatusQuery()
  const overviewQuery = usePortfolioOverviewQuery()

  return computed(() =>
    resolveDashboardState(
      accountQuery.data.value,
      overviewQuery.data.value?.totalInvested.value ?? 0,
    ),
  )
}
